import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFSelect, RHFTextField } from '../../components/hook-form';
import CardRedemptionDialogFrame from './CardRedemptionDialogFrame';

const REJECTION_REASONS = [
  { value: '', label: '-- Select Reason --', prefill: '' },
  {
    value: 'bad_card',
    label: 'Bad card',
    prefill: 'The card you submitted was rejected because it is invalid or unreadable.',
  },
  {
    value: 'used_card',
    label: 'Used card',
    prefill: 'The card you submitted has already been used and cannot be accepted.',
  },
  {
    value: 'incorrect_details',
    label: 'Incorrect details',
    prefill: 'The details provided do not match the card. Please check and resubmit.',
  },
  {
    value: 'additional_info',
    label: 'Requires additional information',
    prefill: 'We need more information to process your card. Please contact support.',
  },
  {
    value: 'no_buyer',
    label: 'No Active Buyer',
    prefill: 'Currently, there is no active buyer for this card type. Please try again later.',
  },
  {
    value: 'duplicate',
    label: 'Duplicate Order',
    prefill: 'This card has already been submitted in a previous order. Duplicate submissions are not allowed.',
  },
  {
    value: 'zombie_card',
    label: 'Zombie Card',
    prefill: 'This voucher is no longer valid so it cannot be exchanged.',
  },
];

const rejectSchema = Yup.object().shape({
  reason: Yup.string().required('Please select a reason'),
  remark: Yup.string().required('Remark is required'),
});

export default function RejectOrderDialog({ loading, onClose, onSubmit, open }) {
  const methods = useForm({
    resolver: yupResolver(rejectSchema),
    defaultValues: { reason: '', remark: '' },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const selectedReason = watch('reason');

  useEffect(() => {
    reset({ reason: '', remark: '' });
  }, [open, reset]);

  useEffect(() => {
    const selectedOption = REJECTION_REASONS.find((reason) => reason.value === selectedReason);
    if (selectedOption?.prefill) {
      setValue('remark', selectedOption.prefill, { shouldValidate: true });
    }
  }, [selectedReason, setValue]);

  return (
    <CardRedemptionDialogFrame
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Reject Order"
      actions={
        <LoadingButton
          variant="contained"
          color="error"
          loading={loading || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Submit Review
        </LoadingButton>
      }
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, boxShadow: 'none' }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Rejection Reason
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a reason for rejecting this card redemption order.
              </Typography>
              <RHFSelect name="reason" sx={{ mt: 1, width: '100%' }}>
                {REJECTION_REASONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RHFSelect>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Remark
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This message will be sent to the user as the reason for rejection. You can edit it if needed.
              </Typography>
              <RHFTextField
                sx={{ mt: 1 }}
                name="remark"
                required
                multiline
                minRows={2}
                maxRows={3}
                placeholder="Type or select a reason above to prefill"
              />
            </Box>
          </Stack>
        </Card>
      </FormProvider>
    </CardRedemptionDialogFrame>
  );
}

RejectOrderDialog.propTypes = {
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};
